/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';

import { useRetracked } from 'js/lib/retracked';

import { Button, IconButton, breakpoints } from '@coursera/cds-core';
import { ArrowPreviousIcon } from '@coursera/cds-icons';
import { track } from '@coursera/event-pulse/sdk';

import MobileNavigationContext from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import { setPageHeaderInfoState } from 'bundles/composable-page-header/stores/PageHeaderInfoStore';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  menuConfig: React.ReactElement;
  homeLogo: React.ReactElement;
  className?: string;
};

const styles = {
  menuButton: css`
    color: var(--cds-color-neutral-primary);
    margin-right: var(--cds-spacing-100);
  `,
  button: css`
    color: var(--cds-color-neutral-primary);
  `,
  backButton: css`
    justify-content: flex-start;
    color: var(--cds-color-grey-600);

    &:focus,
    &:hover {
      color: var(--cds-color-grey-600);
    }
  `,
  fixedHeader: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: var(--cds-spacing-800);
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 var(--cds-spacing-100);
  `,
  greyHeader: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: var(--cds-spacing-800);
    border-bottom: 1px solid var(--cds-color-grey-50);
    background-color: var(--cds-color-grey-25);
    display: flex;
    justify-content: space-between;
    padding: 0 var(--cds-spacing-100);
  `,
  leftLogo: css`
    position: absolute;
    left: 25px;
  `,
  closeButton: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
  `,
  greyCloseButton: css`
    color: var(--cds-color-grey-600);
  `,
  menuConfig: css`
    position: fixed;
    top: var(--cds-spacing-800); /* below the header */
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    padding: 0 var(--cds-spacing-200);

    :has(> .mobile-auth-menu) {
      padding-bottom: 150px;
    }
  `,
  background: (open: boolean) => css`
    background-color: var(--cds-color-neutral-background-primary);
    z-index: 1000;
    width: 100vw;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    transition: transform 0.3s ease;
    transform: ${!open ? 'translateX(-100%)' : 'translateX(0)'};
  `,
};

const MobileMenuIconGridItem = ({ menuConfig, homeLogo, className }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOnSubNav, setIsOnSubNav] = useState(false);

  const trackEvent = useRetracked();

  const setSubNav = (onSubNav: boolean) => {
    setIsOnSubNav(onSubNav);
  };

  const closeMenu = useCallback<() => void>(() => {
    setIsOnSubNav(false);
    setIsOpen(false);
  }, []);

  const openMenu = () => {
    track('open_navigation_menu', {
      navigationType: 'megamenu',
    });
    setIsOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const mediaQuery = window.matchMedia(`(min-width: ${breakpoints.values.md}px)`);
    const handleResize = (e: MediaQueryListEvent) => {
      if (e.matches) closeMenu();
    };

    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, [isOpen, closeMenu]);

  // Syncs every close path (drawer close button, Escape, desktop breakpoint resize) to the
  // header store, so MobileWebConversationalDiscoveryChatContainer's tray z-index is correct
  // regardless of how the drawer closed (MERCH-3449).
  useEffect(() => {
    setPageHeaderInfoState({ isMobileNavOpen: isOpen });
    return () => setPageHeaderInfoState({ isMobileNavOpen: false });
  }, [isOpen]);

  return (
    <nav>
      <IconButton
        size="medium"
        intent="menu"
        variant="ghost"
        onClick={() => {
          trackEvent({
            trackingName: `header_right_nav_button`,
            action: 'click',
            trackingData: {
              name: 'open-nav',
            },
          });
          openMenu();
        }}
        css={styles.menuButton}
        className={classNames('mobile-menu-hamburger', className)}
        aria-label={_t('Open Navigation Menu')}
        data-e2e="mobile-nav-icon"
      />
      {isOpen && (
        <MobileNavigationContext.Provider
          value={{ closeMobileMenu: closeMenu, setIsOnSubNav: setSubNav, onSubNav: isOnSubNav }}
        >
          <FocusTrap
            focusTrapOptions={{
              allowOutsideClick: false,
              initialFocus: document.querySelector('.mobile-header-logo a') as HTMLElement,
            }}
          >
            <div css={styles.background(isOpen)}>
              <div css={isOnSubNav ? styles.greyHeader : styles.fixedHeader}>
                {isOnSubNav ? (
                  <Button
                    variant="ghost"
                    size="medium"
                    iconPosition="before"
                    icon={<ArrowPreviousIcon />}
                    onClick={() => setSubNav(false)}
                    css={styles.backButton}
                    edgeAlign="start"
                  >
                    {_t('Back')}
                  </Button>
                ) : (
                  <div css={styles.leftLogo} className="mobile-header-logo">
                    {homeLogo}
                  </div>
                )}
                <div css={styles.closeButton}>
                  <IconButton
                    size="medium"
                    intent="close"
                    variant="ghost"
                    onClick={closeMenu}
                    css={styles.greyCloseButton}
                  />
                </div>
              </div>
              <ul css={styles.menuConfig} className="menuConfig">
                {menuConfig}
              </ul>
            </div>
          </FocusTrap>
        </MobileNavigationContext.Provider>
      )}
    </nav>
  );
};

export default MobileMenuIconGridItem;
